import { RecentActors, Edit, Delete, Add } from '@mui/icons-material';
import { Grid, useMediaQuery, Theme, Button, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { getSageVendorContacts, deleteVendorContact } from '../../../../fetch';
import { FC, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// Components
import { Card, Table, Loader } from '../../../../components';
import { formatInputPhoneNumber } from '../../../../helpers';
import { ISageVendorContact, IVendorDetail } from '../../../../models';
import { MobileVendorContacts } from './MobileVendorContacts';
import { VendorContactModal } from './VendorContactModal';

interface IVendorContacts {
  currentVendor: IVendorDetail;
}

export const VendorContacts: FC<IVendorContacts> = ({ currentVendor }) => {
  // styles
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 600px)');
  // state
  const [isVendorContactModalOpen, setVendorContactModalOpen] = useState(false);
  const [currentVendorContact, setCurrentVendorContact] = useState<ISageVendorContact | null>(null);
  const [isLoadingSageVendorContacts, setIsLoadingSageVendorContacts] = useState(false);
  const [sageVendorContacts, setSageVendorContacts] = useState<ISageVendorContact[] | null>(null);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  // context
  const { enqueueSnackbar } = useSnackbar();
  // api calls
  const fetchSageVendorContacts = async () => {
    if (currentVendor && currentVendor.vendorId) {
      try {
        setIsLoadingSageVendorContacts(true);
        const res = await getSageVendorContacts(currentVendor.vendorId);
        setSageVendorContacts(res.records);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingSageVendorContacts(false);
      }
    }
  };
  // effects
  useEffect(() => {
    fetchSageVendorContacts();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVendor]);
  // custom functions
  const handleEditContact = (val: ISageVendorContact) => {
    setCurrentVendorContact(val);
    setVendorContactModalOpen(true);
  };

  const handleDelete = async id => {
    const result = window.confirm('Are you sure you want to delete this contact?');
    if (result) {
      setDeleting(true);
      try {
        await deleteVendorContact(id);
        fetchSageVendorContacts();
        enqueueSnackbar('Contact Deleted!', {
          variant: 'success'
        });
      } catch (error) {
        var errorMessage = error?.response?.data?.Detail;
        if (errorMessage) {
          enqueueSnackbar(errorMessage, {
            variant: 'error'
          });
        } else {
          enqueueSnackbar('Unable to delete contact.', {
            variant: 'error'
          });
        }
      } finally {
        setDeleting(false);
      }
    }
  };

  // columns
  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Title',
        accessor: 'jobTitle',
        sort: false
      },
      {
        Header: 'Email',
        accessor: 'email',
        sort: false
      },
      {
        Header: 'Phone Number',
        accessor: 'phoneNumber',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISageVendorContact } };
        }) => {
          return <>{formatInputPhoneNumber(original.phoneNumber)}</>;
        }
      },
      {
        Header: 'Cell Phone Number',
        accessor: 'cellPhoneNumber',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISageVendorContact } };
        }) => {
          return <>{formatInputPhoneNumber(original.cellPhoneNumber)}</>;
        }
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISageVendorContact } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                color="primary"
                startIcon={<Edit />}
                className={classes.editButton}
                onClick={() => {
                  handleEditContact(original);
                }}
              >
                Edit
              </Button>
              <Button
                color="error"
                startIcon={<Delete />}
                onClick={() => {
                  handleDelete(original.sageContactId);
                }}
              >
                Delete
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!sageVendorContacts) {
    return null;
  }

  return (
    <>
      <Card icon={<RecentActors />} title="Contacts">
        {isDeleting && (
          <Loader position="centered" type="fullscreen">
            Deleting...
          </Loader>
        )}
        <Box display="flex" justifyContent="flex-end">
          <Button
            startIcon={<Add />}
            onClick={() => setVendorContactModalOpen(true)}
            color="primary"
            variant="contained"
            className={classes.addUserButton}
          >
            Add Contact
          </Button>
        </Box>
        <Grid container className={classes.tableWrapper}>
          <Grid item xs={12}>
            <Table
              columns={columns}
              data={sageVendorContacts || []}
              isLoading={isLoadingSageVendorContacts}
              hidePagination
              ResponsiveComponent={isMobile ? MobileVendorContacts : undefined}
              mobileProps={{
                handleEdit: val => handleEditContact(val),
                handleDelete: val => handleDelete(val.sageContactId)
              }}
            />
          </Grid>
        </Grid>
      </Card>
      <VendorContactModal
        onClose={() => {
          setVendorContactModalOpen(false);
          setCurrentVendorContact(null);
        }}
        open={isVendorContactModalOpen}
        currentVendorId={currentVendor.vendorId}
        fetchSageVendorContacts={fetchSageVendorContacts}
        currentVendorContact={currentVendorContact}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  tableWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    paddingBottom: 0
  },
  actionButtons: {
    textAlign: 'center'
  },
  editButton: {
    textAlign: 'right'
  },
  addUserWrapper: {
    marginBottom: theme.spacing(1)
  },
  addUserButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  }
}));
